import { useState, useEffect, useCallback } from 'react';
import { Card, Modal, Tag, Empty, Spin, Button, Row, Col, message } from 'antd';
import { CheckCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import FinancialDashboardService from '../../utils/api/financial/FinancialDashboardService';
import ExpenseService from '../../utils/api/financial/expenses';
import './financial-dashboard.css';

const DAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const STATUS_CONFIG = {
  vencido:   { color: '#ff4d4f', label: 'Vencido'  },
  proximo:   { color: '#faad14', label: 'Próximo'  },
  pendiente: { color: '#bfbfbf', label: 'Pendiente'},
  pagado:    { color: '#52c41a', label: 'Pagado'   },
};

const STATUS_TAG = {
  vencido:   'error',
  proximo:   'warning',
  pendiente: 'default',
  pagado:    'success',
};

function buildCalendarGrid(year, month) {

  const first = dayjs(`${year}-${String(month).padStart(2, '0')}-01`);
  const startDow = first.day(); // 0=Sun
  const daysInMonth = first.daysInMonth();
  const prevMonthDays = first.subtract(1, 'month').daysInMonth();

  const cells = [];


  for (let i = startDow - 1; i >= 0; i--) {
    cells.push({ day: prevMonthDays - i, current: false });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, current: true });
  }

  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, current: false });
  }

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

function flattenDayItems(byType = {}) {
  return Object.entries(byType).flatMap(([, td]) =>
    (td.items || []).map((item) => ({ ...item, typeLabel: td.label }))
  );
}

export default function DashboardPaymentCalendar() {
  const today = dayjs();
  const [month, setMonth] = useState(today.month() + 1);
  const [year,  setYear]  = useState(today.year());
  const [days,  setDays]  = useState({});
  const [calLoading, setCalLoading] = useState(false);

  const [modalOpen,  setModalOpen]  = useState(false);
  const [selDate,    setSelDate]    = useState(null);
  const [dayItems,   setDayItems]   = useState([]);
  const [dayLoading, setDayLoading] = useState(false);

  const loadCalendar = useCallback(async (m, y) => {
    setCalLoading(true);
    try {
      const res = await FinancialDashboardService.getCalendar(m, y);
      setDays(res?.data?.days || {});
    } catch {
      setDays({});
    } finally {
      setCalLoading(false);
    }
  }, []);

  useEffect(() => { loadCalendar(month, year); }, [month, year, loadCalendar]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const loadDay = useCallback(async (dateStr) => {
    setDayLoading(true);
    try {
      const res = await FinancialDashboardService.getCalendarDay(dateStr);
      setDayItems(flattenDayItems(res?.data?.by_type));
    } catch {
      setDayItems([]);
    } finally {
      setDayLoading(false);
    }
  }, []);

  const handleDayClick = (day, isCurrent) => {
    if (!isCurrent) return;
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (!days[dateStr]) return;
    setSelDate(dateStr);
    setModalOpen(true);
    loadDay(dateStr);
  };

  const handleMarkPaid = async (item) => {
    const body = item.type === 'supplier'
      ? { obligation_status: 'PAGADO' }
      : { payment_completed: true };
    try {
      await ExpenseService.EditExpense(item.id, body);
      message.success('Pago marcado como pagado');
      await loadDay(selDate);
      loadCalendar(month, year);
    } catch {
      message.error('Error al actualizar el pago');
    }
  };

  const weeks = buildCalendarGrid(year, month);
  const todayStr = today.format('YYYY-MM-DD');

  return (
    <>
      <Card
        className="financial-card"
        title={<span style={{ fontSize: '14px', fontWeight: 'bold' }}>Calendario de Pagos</span>}
        bodyStyle={{ padding: '16px 24px' }}
      >
      
        <div className="pcal-legend">
          {Object.entries(STATUS_CONFIG).map(([k, v]) => (
            <span key={k} className="pcal-legend-item">
              <span className="pcal-dot" style={{ background: v.color }} />
              {v.label}
            </span>
          ))}
        </div>

     
        <div className="pcal-nav">
          <button className="pcal-nav-btn" onClick={prevMonth}><LeftOutlined /></button>
          <span className="pcal-nav-title">{MONTHS_ES[month - 1]} {year}</span>
          <button className="pcal-nav-btn" onClick={nextMonth}><RightOutlined /></button>
        </div>

     
        <Spin spinning={calLoading}>
          <div className="pcal-grid">
           
            {DAYS_ES.map(d => (
              <div key={d} className="pcal-dow">{d}</div>
            ))}

          
            {weeks.flat().map((cell, idx) => {
              const dateStr = cell.current
                ? `${year}-${String(month).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`
                : null;
              const dayData  = dateStr ? days[dateStr] : null;
              const isToday  = dateStr === todayStr;
              const hasEvent = !!dayData;
              const cfg      = dayData ? STATUS_CONFIG[dayData.status] : null;

              return (
                <div
                  key={idx}
                  className={[
                    'pcal-cell',
                    !cell.current   ? 'pcal-cell--other'   : '',
                    isToday         ? 'pcal-cell--today'   : '',
                    hasEvent        ? 'pcal-cell--has-event' : '',
                  ].join(' ')}
                  onClick={() => handleDayClick(cell.day, cell.current)}
                >
                  <span className="pcal-day-num">{cell.day}</span>
                  {cfg && (
                    <span
                      className="pcal-event-dot"
                      style={{ background: cfg.color }}
                      title={`${cfg.label} · ${dayData.count} gasto(s)`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Spin>
      </Card>

      <Modal
        title={selDate ? `Detalles — ${dayjs(selDate).format('DD/MM/YYYY')}` : 'Pagos'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={<Button onClick={() => setModalOpen(false)}>Cerrar</Button>}
        width={720}
        destroyOnClose
      >
        {dayLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : dayItems.length === 0 ? (
          <Empty description="No hay gastos para esta fecha" />
        ) : (
          dayItems.map((item) => {
            const isPaid = item.status === 'pagado';
            const fmtAmt = new Intl.NumberFormat('es-MX', {
              style: 'currency', currency: item.currency || 'MXN', minimumFractionDigits: 2,
            }).format(item.amount || 0);

            return (
              <Row
                key={item.id}
                align="middle"
                gutter={12}
                style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}
              >
                <Col flex="1">
                  <div style={{ fontWeight: 600 }}>{item.name || '—'}</div>
                  <div style={{ fontSize: 12, color: '#aaa' }}>{item.typeLabel}</div>
                </Col>
                <Col>
                  <div style={{ fontWeight: 700 }}>{fmtAmt}</div>
                  <div style={{ fontSize: 11, color: '#ccc', textAlign: 'right' }}>{item.currency || 'MXN'}</div>
                </Col>
                <Col>
                  <Tag color={STATUS_TAG[item.status] || 'default'} style={{ minWidth: 72, textAlign: 'center', margin: 0 }}>
                    {STATUS_CONFIG[item.status]?.label || item.status}
                  </Tag>
                </Col>
                <Col>
                  {isPaid ? (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                  ) : (
                    <Button
                      size="small"
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      style={{ background: '#52c41a', borderColor: '#52c41a' }}
                      onClick={() => handleMarkPaid(item)}
                    >
                      Marcar Pagado
                    </Button>
                  )}
                </Col>
              </Row>
            );
          })
        )}
      </Modal>
    </>
  );
}
